import { DownloadFormat } from "./download-formats";
import BoundingBox from "./bounding-box";
import Vector from "ol/layer/Vector";

class SubsettingParametersBase {

    left: number;
    bottom: number;
    right: number;
    top: number;
    leftName: string;
    bottomName: string;
    rightName: string;
    topName: string;
    formats: DownloadFormat[];
    gridKey: string;
    coordinatesAreValid: boolean;
    boundingBox : BoundingBox
    gridCellsPerDegree: number;
    projection: string;
    displayDecimalPlaces: number;
    pixelsPerUnit: number;
    graticuleLayer: Vector

    constructor(boundingBox : BoundingBox, projection: string, graticuleLayer: Vector,
        leftName: string, bottomName: string, rightName: string, topName: string,
        pixelsPerUnit: number, displayDecimalPlaces: number ) {
        this.left = 0;
        this.bottom = 0;
        this.right = 0;
        this.top = 0;
        this.leftName = leftName;
        this.bottomName = bottomName;
        this.rightName = rightName;
        this.topName = topName;
        this.formats = Array<DownloadFormat>(0);
        this.coordinatesAreValid = false;
        this.boundingBox = boundingBox;
        this.projection = projection;
        this.gridCellsPerDegree = 240;
        this.displayDecimalPlaces = displayDecimalPlaces;
        this.pixelsPerUnit = pixelsPerUnit;
        this.graticuleLayer = graticuleLayer;
    }    

    setAndClipRectangle(left: number, bottom: number,
        right: number, top: number) { 

        // Clip these values
        left = this.clipCoordinate(left, this.boundingBox.left, this.boundingBox.right);
        bottom = this.clipCoordinate(bottom, this.boundingBox.bottom, this.boundingBox.top);
        right = this.clipCoordinate(right, this.boundingBox.left, this.boundingBox.right);
        top = this.clipCoordinate(top, this.boundingBox.bottom, this.boundingBox.top);

        // Validate that lat and lon are in the right order

        this.coordinatesAreValid = false;

        this.left = left;
        this.bottom = bottom;
        this.right = right;
        this.top = top;

        if ((left < right) && (bottom < top))
        {
            this.coordinatesAreValid = true;
        }
    }

    private clipCoordinate(value: number, lowest: number, highest: number): number {

        if (value < lowest) {
            value = lowest;
        }

        if (value > highest) {
            value = highest;
        }

        return value;
    }

    clearRectangle() {

        this.left = 0;
        this.bottom = 0;
        this.right = 0;
        this.top = 0;
        this.coordinatesAreValid = false;
    }

    addFormat(format: DownloadFormat) {

        if (!this.formats.includes(format)) {

            this.formats.push(format);
        }
    }

    removeFormat(format: DownloadFormat) {

        if (this.formats.includes(format)) {

            var index = this.formats.indexOf(format);
            this.formats.splice(index, 1);
        }
    }

    hasFormat(format: DownloadFormat): boolean {

        return this.formats.includes(format);
    }

    estimatedFileSizeMB(): number {

        let total: number = 0;
        let gridCells: number = this.estimatedGridWidth() * this.estimatedGridHeight();
        let bytesPerCell: number;
        let overhead: number;

        for (let f of this.formats) {

            switch (f) {
                case DownloadFormat.gridNetCdf:
                    bytesPerCell = 2;
                    overhead = 500000;
                    break;

                case DownloadFormat.gridGeoTiff:
                    bytesPerCell = 2;
                    overhead = 2000;
                    break;

                case DownloadFormat.gridEsriAscii:
                    bytesPerCell = 18;
                    overhead = 200;
                    break;

                case DownloadFormat.sidNetCdf:
                    bytesPerCell = 1;
                    overhead = 500000;
                    break;

                case DownloadFormat.sidGeoTiff:
                    bytesPerCell = 1;
                    overhead = 2000;
                    break;

                case DownloadFormat.sidEsriAscii:
                    bytesPerCell = 3;
                    overhead = 200;
                    break;

                // These jpeg/png estimates are very rough, based on test data
                case DownloadFormat.colorJpeg:
                    bytesPerCell = 0.02;
                    overhead = 0;
                    break;
                case DownloadFormat.colorPng:
                    bytesPerCell = 0.02;
                    overhead = 0;
                    break;
                case DownloadFormat.shadedReliefJpeg:
                    bytesPerCell = 0.1;
                    overhead = 0;
                    break;
                case DownloadFormat.shadedReliefPng:
                    bytesPerCell = 0.1;
                    overhead = 0;
                    break;
                // end of rough estimates
            }

            total += overhead + (gridCells * bytesPerCell);
        }

        return Math.ceil(total / 1000000);
    }

    estimatedGridWidth(): number {

        if (this.coordinatesAreValid) {
            let width = this.right - this.left;
            return Math.round(width * this.pixelsPerUnit);
        }

        return 0;
    }

    estimatedGridHeight(): number {

        if (this.coordinatesAreValid) {
            let height = this.top - this.bottom;
           return Math.round(height * this.pixelsPerUnit);
        }

        return 0;
    }
}

export default class SubsettingParameters extends SubsettingParametersBase{
    // ...
    
}

